/**
 * Capa de presentación del dashboard: color por plan, tema de los gráficos y
 * formato de números. Vive en un solo archivo para que la torta, las barras y
 * las tablas se lean como un mismo sistema; antes cada componente tenía su
 * propia copia de la paleta y de `formatCurrency`, y se desincronizaban.
 */

// ── Paleta por plan ──────────────────────────────────────────────────────────

/**
 * Paleta de gráfico, distinta a la de identidad de `data/planes.ts` a
 * propósito: aquella tiene gris, negro y beige, que como serie categórica no
 * se distinguen entre sí ni sobre blanco. Acá cada plan necesita leerse solo.
 */
const PLAN_COLORS: Record<string, string> = {
  'Executive 3000': '#2563eb',
  'Premium 3030': '#06b6d4',
  'Active 7000': '#ec4899',
  Priority: '#8b5cf6',
  'Exclusive 8080': '#f59e0b',
  'Joven 8000': '#10b981',
};

const FALLBACK_COLOR = '#64748b';

export function planColor(plan: string): string {
  return PLAN_COLORS[plan] ?? FALLBACK_COLOR;
}

/** Mismo color con transparencia, para las series de bajas. */
export function withAlpha(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// ── Formato ──────────────────────────────────────────────────────────────────

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatNumber(value: number): string {
  return value.toLocaleString('es-AR');
}

/** Números de eje: 1.2 M / 340 k, para que las etiquetas no se pisen. */
export function formatCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace('.0', '')} M`;
  if (abs >= 1_000) return `${Math.round(value / 1_000)} k`;
  return formatNumber(Math.round(value));
}

/** Entero con signo explícito: el signo es parte del dato, no decoración. */
export function formatSigned(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${formatNumber(Math.abs(value))}`;
}

// ── Tema de Chart.js ─────────────────────────────────────────────────────────

const FONT_FAMILY = "'Nunito Sans', sans-serif";

const INK = '#64748b'; // slate-500
const MUTED = '#94a3b8'; // slate-400
const GRID = '#f1f5f9'; // slate-100
const ZERO_LINE = '#cbd5e1'; // slate-300

/** Leyenda: puntos en vez de cajas, alineada con los puntos del resto de la UI. */
export const legendStyle = {
  position: 'top' as const,
  align: 'end' as const,
  labels: {
    usePointStyle: true,
    pointStyle: 'circle' as const,
    boxWidth: 8,
    boxHeight: 8,
    padding: 16,
    color: INK,
    font: { family: FONT_FAMILY, size: 11 },
  },
};

/** Tooltip oscuro, sin borde ni flecha: se lee sobre cualquier serie. */
export const tooltipStyle = {
  backgroundColor: '#0f172a',
  titleColor: '#f8fafc',
  bodyColor: '#e2e8f0',
  titleFont: { family: FONT_FAMILY, size: 12, weight: 600 as const },
  bodyFont: { family: FONT_FAMILY, size: 12 },
  padding: 10,
  cornerRadius: 8,
  displayColors: true,
  usePointStyle: true,
  boxPadding: 4,
};

export const tickStyle = {
  color: MUTED,
  font: { family: FONT_FAMILY, size: 11 },
};

export const axisTitleStyle = {
  color: MUTED,
  font: { family: FONT_FAMILY, size: 11 },
};

/** Grilla horizontal tenue, con la línea del cero un punto más marcada. */
export const yGrid = {
  color: (ctx: { tick?: { value: number } }) => (ctx.tick?.value === 0 ? ZERO_LINE : GRID),
  drawTicks: false,
};
